/**
 * The tape format, decoded in the browser.
 *
 * A tape payload is the runtime code of one `ImmortalFruitFliesTape` contract
 * (`eth_getCode(tape)`), and it is written by `tools/tapes.py` - the same bytes
 * the chain carries. Big-endian throughout, 16-bit where it can be:
 *
 *   uint16[n+1]  absolute offsets into the payload; the FIRST one doubles as the
 *                header (n = first / 2 - 1), which is how the file states its own
 *                neuron count
 *   5-byte record one synapse: 3-byte GLOBAL target neuron id, 2-byte signed
 *                weight, all records of one source neuron contiguous
 *
 * A source neuron is `tapeStartNeuron + index`, so a tape is addressed in slots,
 * not in ids: that is why `script/TapeList.sol` carries a start per tape.
 */

export type Synapse = {
  readonly source: number;
  readonly target: number;
  readonly weight: number;
};

export type TapeDecode = {
  /** source neurons this tape covers */
  readonly neurons: number;
  readonly synapses: number;
  readonly targets: number;
  readonly lightest: number;
  readonly heaviest: number;
  /** how many records the first covered neuron has, as a spot check */
  readonly firstNeuronSynapses: number;
};

function u16(bytes: Uint8Array, at: number): number {
  return (bytes[at] << 8) | bytes[at + 1];
}

function u24(bytes: Uint8Array, at: number): number {
  return (bytes[at] << 16) | (bytes[at + 1] << 8) | bytes[at + 2];
}

/** The synapses of one tape, neuron by neuron. Throws when the payload is not a tape. */
export function decodeTape(bytes: Uint8Array, startNeuron = 0): Synapse[] {
  if (bytes.length < 4) throw new Error("tape: too short to carry a header");

  const neurons = u16(bytes, 0) / 2 - 1;

  if (!Number.isInteger(neurons) || neurons <= 0)
    throw new Error(`tape: header says ${neurons} neurons`);

  const offsets: number[] = [];

  for (let at = 0; at <= neurons; at += 1) {
    if (at * 2 + 1 >= bytes.length) throw new Error("tape: offset table runs past the payload");

    offsets.push(u16(bytes, at * 2));
  }

  if (offsets[offsets.length - 1] > bytes.length)
    throw new Error("tape: offsets point past the payload");

  const synapses: Synapse[] = [];

  for (let neuron = 0; neuron < neurons; neuron += 1) {
    const from = offsets[neuron];
    const to = offsets[neuron + 1];

    if (to < from || (to - from) % 5 !== 0)
      throw new Error(`tape: neuron ${neuron} carries ${to - from} bytes, not 5-byte records`);

    for (let at = from; at < to; at += 5) {
      const raw = u16(bytes, at + 3);

      synapses.push({
        source: startNeuron + neuron,
        target: u24(bytes, at),
        weight: raw >= 32_768 ? raw - 65_536 : raw,
      });
    }
  }

  return synapses;
}

/** What the page prints about a decoded tape: shape, not a synapse dump. */
export function summarise(synapses: readonly Synapse[], neurons: number): TapeDecode {
  const targets = new Set<number>();
  let lightest = Number.POSITIVE_INFINITY;
  let heaviest = Number.NEGATIVE_INFINITY;
  let firstNeuronSynapses = 0;

  for (const synapse of synapses) {
    targets.add(synapse.target);
    lightest = Math.min(lightest, synapse.weight);
    heaviest = Math.max(heaviest, synapse.weight);

    if (synapse.source === synapses[0]?.source) firstNeuronSynapses += 1;
  }

  return {
    neurons,
    synapses: synapses.length,
    targets: targets.size,
    lightest: synapses.length === 0 ? 0 : lightest,
    heaviest: synapses.length === 0 ? 0 : heaviest,
    firstNeuronSynapses,
  };
}

/** The neuron count a payload states about itself, without decoding it. */
export function tapeNeurons(bytes: Uint8Array): number {
  if (bytes.length < 2) throw new Error("tape: too short to carry a header");

  return u16(bytes, 0) / 2 - 1;
}
